import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageSquare, GitBranch, Handshake, CheckCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const steps = [
  { icon: MessageSquare, num: "01", title: "Talk to AvA", desc: "Start a conversation with AvA, Apexum's AI-powered clinical access connector. She'll guide you through a brief intake — your clinic type, provider status, services of interest, and monthly volume." },
  { icon: GitBranch, num: "02", title: "Complete Clinic Qualification", desc: "AvA collects structured information about your organization, compliance acknowledgments, and pathway needs. The process is designed for licensed clinical operators." },
  { icon: CheckCircle, num: "03", title: "Get Routed to the Right Pathway", desc: "Based on your intake, AvA routes your profile to the appropriate Apexum access pathway — 503A/503B pharmacy, lab integration, telehealth infrastructure, fulfillment coordination, or live Apexum team handoff." },
  { icon: Handshake, num: "04", title: "Move Forward with Trusted Relationships", desc: "Apexum supports qualified clinics through established pharmacy and infrastructure relationships. No upfront onboarding fee for qualified organizations." },
];

const pathways = [
  { title: "Pharmacy Pathway Routing", desc: "503A and 503B compounding pharmacy access for qualified clinic operators." },
  { title: "Lab Integration", desc: "Connect with trusted lab partners for patient testing and diagnostics support." },
  { title: "Telehealth Support", desc: "Infrastructure pathways for telehealth-first and hybrid clinical models." },
  { title: "Fulfillment Coordination", desc: "Patient-specific and office-use fulfillment routing with trusted partners." },
  { title: "Live Apexum Handoff", desc: "Direct introduction to the Apexum team for complex or high-volume clinic needs." },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="pt-28 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-4">How It Works</p>
          <h1 className="text-5xl font-medium text-slate-900 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
            A simpler path into trusted clinical infrastructure
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            Talk to AvA. Get qualified. Route to the right pathway. Build with backend support.
          </p>
          <Link to="/talk-to-ava"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3.5 rounded-full font-medium hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/25">
            Start with AvA <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="space-y-6">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                  className="flex gap-6 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-7">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-medium text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit, sans-serif' }}>
            One front door. Multiple trusted pathways.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pathways.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h4 className="font-semibold text-slate-900 mb-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</h4>
                <p className="text-sm text-slate-500">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
